import { computed, ref, watch } from 'vue'
import { useNow } from '@/composables/game/time'
import type { PathwayEvent, StepReward } from '@/types/shared/pathway-event'
import { useConfig } from '@/composables/game/config'
import { usePlayer } from '@/composables/game/player'
import { backButtonCallback } from '@/router'
import type { Nullable } from '@/types/shared/common'
import { useLocalStorage } from '@vueuse/core'
import { buildDropeeStorageKey } from '@/config/config'
import { ECOMMERCE_ITEM_BENEFIT_TYPE, ECOMMERCE_ITEM_TAG, PATHWAY_EVENT_TYPE } from '@/types/shared/player-config'
import { isTodayDate } from '@/helpers/date'
import { DateTime } from 'luxon'

export type FrontendPathwayEvent = PathwayEvent & { hidden: boolean }

export const usePathwayEvents = (eventType: PATHWAY_EVENT_TYPE = PATHWAY_EVENT_TYPE.PATHWAY) => {
  const { player } = usePlayer()
  const { config } = useConfig()
  const { now } = useNow()

  const pathwayEvents = computed((): FrontendPathwayEvent[] =>
    config.value.game.pathwayEvents.filter(event => {
      const progress = player.value.pathwayEvents.runningEvents[event.id]
      if (!progress) return false

      return (event.eventType ?? PATHWAY_EVENT_TYPE.PATHWAY) === eventType && event.startDate <= now.value && event.endDate >= now.value
    }).map(event => {
      const progress = player.value.pathwayEvents.runningEvents[event.id]

      return {
        ...event,
        hidden: progress.currentStep >= Object.keys(event.config.steps).length,
      }
    },
    ),
  )

  const activePathwayEvents = computed(() => pathwayEvents.value.filter(event => !event.hidden))
  const anyActivePathwayEvent = computed(() => activePathwayEvents.value.length > 0)

  return {
    pathwayEvents,
    activePathwayEvents,
    anyActivePathwayEvent,
  }
}

export type TotalRewards = Record<keyof Omit<StepReward, 'specialCurrencies'>, number> & {
  specialCurrencies: Record<string, number>
}

export const usePathwayEvent = (pathwayEvent: PathwayEvent) => {
  const { now } = useNow()
  const { syncConfig } = useConfig()
  const { player, updatePlayer } = usePlayer()

  const maybePlayerProgress = computed(() => player.value.pathwayEvents.runningEvents[pathwayEvent.id])
  const playerProgress = computed(() => {
    if (!maybePlayerProgress.value) throw new Error('Player pathway event progress not found for the id ' + pathwayEvent.id)
    return maybePlayerProgress.value
  })

  const countdownForEventEnd = computed(() => {
    const eventEndTime = playerProgress.value.startTimestamp + playerProgress.value.duration
    return Math.max(eventEndTime - now.value, 0)
  })

  const hasFinishedCurrentProgress = computed(() => playerProgress.value.currentStep >= Object.keys(pathwayEvent.config.steps).length)

  const totalRewards = computed(() => {
    return Object.values(pathwayEvent.config.steps).reduce<TotalRewards>((acc, step) => {
      const reward = step.reward

      acc.goldenCoins += reward.goldenCoins ?? 0
      acc.spins += reward.spins ?? 0
      acc.giveawayTickets += reward.giveawayTickets ?? 0
      acc.dropeeStars += reward.dropeeStars ?? 0

      if (reward.specialCurrencies) {
        Object.entries(reward.specialCurrencies).forEach(([currencyId, amount]) => {
          acc.specialCurrencies[currencyId] = (acc.specialCurrencies[currencyId] ?? 0) + (amount ?? 0)
        })
      }

      return acc
    }, {
      goldenCoins: 0,
      spins: 0,
      giveawayTickets: 0,
      dropeeStars: 0,
      specialCurrencies: {} as Record<string, number>,
    })
  })

  watch(countdownForEventEnd, async countdownForEventEnd => {
    if (countdownForEventEnd === 0) {
      setTimeout(async () => {
        const { playerStats } = await syncConfig()
        updatePlayer(playerStats)
      }, 1000)
    }
  })

  return {
    countdownForEventEnd,
    playerProgress,
    hasFinishedCurrentProgress,
    totalRewards,
  }
}

const selectedPathwayEvent = ref<PathwayEvent | null>(null)
export const usePathwayEventOverlay = () => {

  const showPathwayEventOverlay = computed(() => !!selectedPathwayEvent.value)

  function hidePathwayEventOverlay() {
    backButtonCallback.value = null
    selectedPathwayEvent.value = null
  }

  function setSelectedPathwayEvent(event: PathwayEvent) {
    backButtonCallback.value = hidePathwayEventOverlay
    selectedPathwayEvent.value = event
  }

  function showFirstPathway(pathwayEvents: FrontendPathwayEvent[]) {
    setSelectedPathwayEvent(pathwayEvents.find(event => !event.hidden) as PathwayEvent)
  }

  return {
    showPathwayEventOverlay,
    hidePathwayEventOverlay,
    setSelectedPathwayEvent,
    selectedPathwayEvent,
    showFirstPathway,
  }
}

export const useOutOfSpecialCurrency = () => {
  const { config } = useConfig()
  const { nowDateIso } = useNow()
  const selectedSpecialCurrencyForModal = ref<Nullable<string>>(null)
  const modalCloseDate = useLocalStorage<Record<string, string>>(buildDropeeStorageKey('pathwayOutOfCurrencyModalCloseDate'), {})

  function getAvailableEcommerceItems(currencyId: string) {
    return config.value.game.ecommerce.items.filter(item => item.tags.includes(ECOMMERCE_ITEM_TAG.OUT_OF_SPECIAL_CURRENCY) && item.benefits.some(b => b.type === ECOMMERCE_ITEM_BENEFIT_TYPE.SPECIAL_CURRENCY && b.currency === currencyId) && !item.archivedAt)
  }

  function canShowOutOfSpecialCurrencyModal(currencyId: string) {
    const ecommerceItems = getAvailableEcommerceItems(currencyId)
    if (!ecommerceItems.length) return false
    if (modalCloseDate.value[currencyId] !== undefined) {
      return !isTodayDate(DateTime.fromISO(modalCloseDate.value[currencyId]))
    }
    return true
  }

  function closeOutOfSpecialCurrencyModal(hideForToday: boolean) {
    if (hideForToday && selectedSpecialCurrencyForModal.value) {
      modalCloseDate.value[selectedSpecialCurrencyForModal.value] = nowDateIso.value
    }
    selectedSpecialCurrencyForModal.value = null
  }

  return {
    canShowOutOfSpecialCurrencyModal,
    selectedSpecialCurrencyForModal,
    closeOutOfSpecialCurrencyModal,
    getAvailableEcommerceItems,
  }
}